import type { MsContact, MsMessage, MsThread, MsTopicKey } from '@elva/ui';
import type { PatientSearchResult } from '../../patients/api/types';
import type { SmsConversation, SmsThread, SmsThreadMessage } from '../api/types';

export function normalizePhoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

export function phonesMatch(a: string, b: string): boolean {
  const da = normalizePhoneDigits(a);
  const db = normalizePhoneDigits(b);
  if (!da || !db) return false;
  if (da === db) return true;
  if (da.length >= 10 && db.length >= 10) {
    return da.slice(-10) === db.slice(-10);
  }
  return false;
}

export function findConversationByPhone(
  conversations: SmsConversation[],
  phone: string,
): SmsConversation | undefined {
  return conversations.find((c) => phonesMatch(c.patient_phone, phone));
}

export function adaptPatientSearchToMsContact(result: PatientSearchResult): MsContact | null {
  const phone = result.phone?.trim();
  if (!phone) return null;
  const name = result.name.trim() || 'Unknown';
  return {
    id: String(result.id),
    name,
    phone,
    initials: initials(name),
    lang: 'English',
  };
}

type MsPatientProfile = MsThread['patient'];

function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  return (parts[0]?.slice(0, 2) ?? '??').toUpperCase();
}

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function mapLanguage(detectedLanguage: string | null | undefined): string {
  const normalized = (detectedLanguage ?? '').trim().toLowerCase();
  if (!normalized || normalized === 'english' || normalized === 'en') return 'en';
  if (normalized === 'spanish' || normalized === 'es') return 'Spanish';
  return detectedLanguage?.trim() || 'en';
}

function mapHealth(health: string): MsThread['health'] {
  if (health === 'green' || health === 'yellow' || health === 'red') return health;
  return 'neutral';
}

// The fixed V1 topic vocabulary (SMS spec §2). Tags were removed (spec D8) —
// classification is topics only, set by the backend per message/episode.
const TOPIC_KEYS: readonly MsTopicKey[] = [
  'emergency',
  'booking',
  'billing',
  'insurance',
  'new_patient',
  'clinical_question',
  'records_forms',
  'complaint',
  'feedback',
  'spam',
];

/** Map a backend topic string to a known topic key, or null if unknown/absent. */
function mapTopicKey(topic: string | null | undefined): MsTopicKey | null {
  if (!topic) return null;
  return (TOPIC_KEYS as readonly string[]).includes(topic) ? (topic as MsTopicKey) : null;
}

function mapDeliveryStatus(status: string): MsMessage['deliveryStatus'] | undefined {
  if (status === 'sent' || status === 'delivered' || status === 'failed') return status;
  return undefined;
}

function mapMessageWho(message: SmsThreadMessage): MsMessage['who'] {
  if (message.direction === 'inbound') return 'patient';
  const sender = message.sender?.toLowerCase() ?? '';
  if (sender.includes('elva')) return 'elva';
  return 'staff';
}

function defaultPatient(name: string, id: number, phone: string, lang: string): MsPatientProfile {
  return {
    name,
    phone,
    initials: initials(name),
    age: 0,
    gender: '',
    patientId: String(id),
    lang,
    status: 'active',
    next: null,
    balance: '$0',
    insurance: { plan: '', status: '', remaining: 0, expires: '' },
    lastVisit: null,
    consent: { sms: true, email: true },
  };
}

export function adaptSmsConversation(conv: SmsConversation): MsThread {
  const name = conv.patient_name?.trim() || 'Unknown';
  const lang = mapLanguage(conv.detected_language);
  const autoRespondOn =
    conv.auto_respond_state === 'elva_on' && !conv.staff_taken_over && !conv.opted_out;

  return {
    id: conv.id,
    name,
    phone: conv.patient_phone,
    initials: initials(name),
    lang,
    when: conv.time || formatWhen(conv.last_message_at),
    unread: conv.unread ? 1 : 0,
    currentTopic: mapTopicKey(conv.current_topic),
    topics: [],
    health: mapHealth(conv.health),
    autoRespondOn,
    optedOut: conv.opted_out,
    preview: conv.last_message_preview ?? '',
    patient: defaultPatient(name, conv.patient_id, conv.patient_phone, lang),
    summary: conv.last_message_preview ?? '',
    actions: [],
    opportunities: [],
    messages: [],
    ...(conv.subject_patient_name
      ? {
          subject: {
            name: conv.subject_patient_name.trim() || 'Unknown',
            initials: initials(conv.subject_patient_name),
            relation: '',
            patientId: conv.subject_patient_id != null ? String(conv.subject_patient_id) : '',
          },
        }
      : {}),
  };
}

export function adaptSmsThread(base: MsThread, thread: SmsThread): MsThread {
  const lang = mapLanguage(thread.language ?? base.lang);
  const patientName = thread.patient_name?.trim() || base.patient.name || 'Unknown';
  const messages: MsMessage[] = (thread.messages ?? []).map((m) => {
    const deliveryStatus = mapDeliveryStatus(m.delivery_status);
    const who = mapMessageWho(m);
    const message: MsMessage = {
      id: m.id,
      who,
      time: formatWhen(m.created_at),
      text: m.body,
    };
    const senderName = m.sender?.trim();
    if (senderName && who !== 'patient') message.senderName = senderName;
    const topicKey = mapTopicKey(m.topic);
    if (topicKey) message.topic = topicKey;
    if (m.translated_body) message.textTranslated = m.translated_body;
    if (deliveryStatus) message.deliveryStatus = deliveryStatus;
    return message;
  });

  const episodeSummary = thread.open_episode?.summary ?? thread.enrichment?.live_summary ?? '';
  return {
    ...base,
    id: thread.id,
    lang,
    patient: defaultPatient(
      patientName,
      thread.patient_id || Number(base.patient.patientId) || 0,
      base.patient.phone,
      lang,
    ),
    messages,
    // The "Live summary" card shows the open episode's summary (empty ⇒ offer to generate one).
    summary: episodeSummary,
    episodeId: thread.open_episode?.id ?? null,
  };
}
